import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button, IconButton, Spinner } from '@primer/react';
import { XIcon } from '@primer/octicons-react';

// Shared shell for the three one-time subscription modals (TrialWelcomeModal,
// ApplicationSubmittedModal, ApprovalModal), and — from C4, 2026-09-07 — for
// the two action modals in the search bar (SaveSearchModal, CreateAlertModal).
//
// NOTE: Uses a plain div portal rather than Primer's Dialog — Primer's Dialog
// (v38) forces a "Dialog" title header and CSS-variable-dependent background
// that could not be suppressed within its API. All interactive elements inside
// (Button, IconButton, Spinner) are still Primer components.
//
// DISMISS IS SEPARATE FROM THE PRIMARY ACTION (C4, 2026-09-07). It was not, and
// could not be while every consumer was an announcement whose primary action WAS
// "I've read this": ESC, the backdrop and the X all called `onPrimary`. A form
// modal breaks that — pressing ESC would have SAVED. `onDismiss` defaults to
// `onPrimary`, so the four announcement modals behave exactly as before and
// none of them needed changing.
export default function SubscriptionModal({
  isOpen,
  badge,
  eyebrow,
  heading,
  children,
  primaryLabel,
  onPrimary,
  // Optional: what ESC, the backdrop and the X do. Defaults to onPrimary, which
  // is what every announcement modal wants.
  onDismiss,
  // Optional second button, under the primary. For a form's Cancel.
  secondaryLabel,
  onSecondary,
  // The announcement modals are all 540 and all centred. A form wants room and
  // left-aligned labels.
  width = 540,
  align = 'center',
  footer,
  isLoading,
  // 'neutral' (the default) keeps the announcement modals' grey button;
  // 'primary' gives Primer's primary colour, for a modal whose button commits
  // to something — Save view, Edit view, Create alert.
  primaryVariant = 'neutral',
  // For a form whose primary action cannot run yet. Defaults false, so the
  // announcement modals' "Got it" is always live.
  primaryDisabled = false,
}) {
  const dismiss = onDismiss || onPrimary;

  // ESC key closes the modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, dismiss]);

  // A MENU OPENED INSIDE THE MODAL HAS TO SIT ON TOP OF IT (Mischa, 2026-09-22,
  // who found the Save view pickers opening BEHIND the modal).
  //
  // Primer portals every overlay — ActionMenu, AnchoredOverlay, Tooltip — into
  // one `#__primerPortalRoot__` div it appends to document.body, and it gives
  // that div position/top/left/width and NO z-index (see @primer/react's
  // Portal.js). This modal is its own portal at z-index 1100, and two
  // positioned siblings with no z-index of their own stack in DOM order — the
  // modal is appended later, so it wins, and the menu renders underneath it.
  //
  // So while a modal is open the shared portal root is lifted above it, and put
  // back exactly as it was when the modal closes. It is raised only for as long
  // as a modal is open, which is the only time the question arises.
  useEffect(() => {
    if (!isOpen) return undefined;

    // Primer creates that div on the first overlay it renders, which may be
    // AFTER this runs — the first menu opened inside the modal. It reuses an
    // element that already carries the id (ensureDefaultPortal looks it up by
    // getElementById before creating one), so making it here with the same
    // three properties Primer gives it is the supported way to be early.
    let root = document.getElementById('__primerPortalRoot__');
    if (!root) {
      root = document.createElement('div');
      root.setAttribute('id', '__primerPortalRoot__');
      root.style.position = 'absolute';
      root.style.top = '0';
      root.style.left = '0';
      root.style.width = '100%';
      document.body.appendChild(root);
    }

    const previous = root.style.zIndex;
    root.style.zIndex = '1200';

    return () => {
      root.style.zIndex = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop — clicking it dismisses */}
      <div
        onClick={dismiss}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--overlay-backdrop-bgColor)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Modal — stop propagation so body clicks don't hit backdrop */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="subscription-modal-heading"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width,
            maxWidth: '92vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            backgroundColor: 'var(--bgColor-default)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-floating-large)',
            textAlign: align,
            // The modal portals to document.body, so it doesn't inherit a font
            // from any themed wrapper. In the authenticated app the global Primer
            // CSS sets the body font, but on the Civic Lens public pages (react-brand)
            // it would fall back to the browser's serif default. Set the Primer
            // system stack explicitly — resolves to the same --fontStack-system the
            // app already uses where that token is loaded, and to the literal stack
            // (the fallback) on the public pages where it isn't.
            fontFamily:
              'var(--fontStack-system, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif)',
          }}
        >
          {/* Close button — absolutely positioned top-right */}
          <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
            <IconButton
              icon={XIcon}
              variant="invisible"
              aria-label="Close"
              onClick={dismiss}
            />
          </div>

          <div style={{ padding: '40px 44px 30px' }}>
            {/* Badge area */}
            {badge && (
              <div style={{ margin: '0 auto 18px', lineHeight: 0, display: 'inline-block' }}>
                {badge}
              </div>
            )}

            {/* Eyebrow */}
            {eyebrow && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.13em',
                  textTransform: 'uppercase',
                  color: 'var(--fgColor-accent)',
                }}
              >
                {eyebrow}
              </div>
            )}

            {/* Heading */}
            <h2
              id="subscription-modal-heading"
              style={{
                margin: '10px 0 0',
                fontSize: 27,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--fgColor-default)',
              }}
            >
              {heading}
            </h2>

            {/* Body slot */}
            {children}

            {/* Primary action */}
            <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={onPrimary}
                disabled={isLoading || primaryDisabled}
                variant={primaryVariant === 'primary' ? 'primary' : undefined}
                style={{
                  width: 390,
                  maxWidth: '100%',
                  // THE GREY IS THE ANNOUNCEMENT MODALS' (Mischa, 2026-09-22,
                  // who read it as disabled on a form). Those four modals say
                  // "Got it" on something already decided, and a neutral button
                  // is right for them. A form's primary action is a real
                  // commitment and takes Primer's own primary colour — so the
                  // paint is applied only in the neutral case, rather than
                  // overriding the variant's background with grey.
                  ...(primaryVariant === 'primary'
                    ? {}
                    : {
                        backgroundColor: 'var(--bgColor-neutral-emphasis)',
                        borderColor: 'var(--bgColor-neutral-emphasis)',
                        color: 'var(--fgColor-onEmphasis)',
                      }),
                  borderRadius: '8px',
                  padding: '13px 28px',
                  fontSize: 15,
                  fontWeight: 600,
                  height: 'auto',
                }}
              >
                {isLoading ? <Spinner size="small" /> : primaryLabel}
              </Button>
            </div>

            {/* Optional secondary action, under the primary */}
            {secondaryLabel && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="invisible"
                  onClick={onSecondary || dismiss}
                  disabled={isLoading}
                  style={{ width: 390, maxWidth: '100%' }}
                >
                  {secondaryLabel}
                </Button>
              </div>
            )}

            {/* Footer (optional) */}
            {footer && (
              <div
                style={{
                  marginTop: 18,
                  fontSize: 12.5,
                  color: 'var(--fgColor-muted)',
                }}
              >
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
